// Cart store: the visitor's bag, kept in localStorage, keyed by PlazaStack
// variant id. There is no server-side cart. At checkout the lines are sent to
// PlazaStack (POST /checkout), which prices them itself, reserves stock and
// returns a Stripe-hosted checkout URL. Prices held here are for display only.
//
// Exposes (same shape the old Wix store had, so the header badge, drawer and
// cart page keep working):
//   useCart()                  React hook, snapshot updates
//   addLine(line, qty)         add or merge
//   removeLine(variantId)      remove
//   setLineQty(variantId, n)   change quantity
//   removePurchasedLines(b)    after a paid order: only lines still holding the bought qty
//   syncCartWithCatalog()      refresh prices / availability from /api/cart-sync
//
// Cross-tab sync: the `storage` event re-reads the bag when another tab edits it.

'use client';

import { useSyncExternalStore } from 'react';
import { trackRemoveFromCart } from './analytics';
import { shippingCentsFor } from './policies';
import { MAX_LINES, MAX_QTY_PER_LINE, lineCap, overCap, planAdd, removeBought, type BoughtLine } from './cart-math';

const STORAGE_KEY = 'np:cart:v2';
export { MAX_LINES, MAX_QTY_PER_LINE };

export interface CartLine {
  variantId:    string;
  productId:    string;
  slug:         string;
  name:         string;
  /** Variant label (e.g. "10mL"), null for single-variant products. */
  variantTitle: string | null;
  image?:       string;
  priceCents:   number;
  qty:          number;
  /** Highest quantity one order can hold: in-stock units plus open pre-order capacity (max 10). */
  maxQty:       number;
  /** Units currently in stock; any quantity beyond this ships as pre-order. */
  inStockQty?:  number;
  /** The variant has open pre-order capacity. */
  preorder:     boolean;
  shipsBy?:     string | null;
  brand?:       string;
  /** Set by syncCartWithCatalog when the variant can no longer be bought. */
  unavailable?: boolean;
}

export interface CartSnapshot {
  status:        'loading' | 'ready' | 'empty';
  lines:         CartLine[];
  count:         number;
  subtotalCents: number;
  shippingCents: number;
  hasUnavailable: boolean;
  /** A line asks for more than can be ordered right now; checkout stays blocked until the shopper lowers it. */
  hasOverCap:    boolean;
}

const LOADING: CartSnapshot = { status: 'loading', lines: [], count: 0, subtotalCents: 0, shippingCents: 0, hasUnavailable: false, hasOverCap: false };

function derive(lines: CartLine[]): CartSnapshot {
  if (lines.length === 0) return { status: 'empty', lines: [], count: 0, subtotalCents: 0, shippingCents: 0, hasUnavailable: false, hasOverCap: false };
  const buyable = lines.filter((l) => !l.unavailable);
  const subtotalCents = buyable.reduce((s, l) => s + l.priceCents * l.qty, 0);
  return {
    status: 'ready',
    lines,
    count: lines.reduce((s, l) => s + l.qty, 0),
    subtotalCents,
    shippingCents: shippingCentsFor(subtotalCents),
    hasUnavailable: lines.some((l) => l.unavailable),
    hasOverCap: buyable.some(overCap),
  };
}

let snapshot: CartSnapshot = LOADING;
const listeners = new Set<() => void>();
let initialized = false;

function emit() { for (const fn of listeners) fn(); }

function read(): CartLine[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (l): l is CartLine =>
        l && typeof l.variantId === 'string' && typeof l.priceCents === 'number' && typeof l.qty === 'number' && l.qty > 0,
    );
  } catch {
    return [];
  }
}

function write(lines: CartLine[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch {
    // Private mode / quota: the in-memory snapshot still works for this tab.
  }
  snapshot = derive(lines);
  emit();
}

function init() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  snapshot = derive(read());
  emit();
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      snapshot = derive(read());
      emit();
    }
  });
}

function subscribe(fn: () => void): () => void {
  init();
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useCart(): CartSnapshot {
  return useSyncExternalStore(subscribe, () => snapshot, () => LOADING);
}

/** Current lines without subscribing (event handlers). */
export function getCartLines(): CartLine[] {
  init();
  return snapshot.lines;
}

export type AddResult = { ok: true; qty: number } | { ok: false; reason: 'max' | 'lines' };

export function addLine(line: Omit<CartLine, 'qty' | 'unavailable'>, qty = 1): AddResult {
  init();
  const lines = [...snapshot.lines];
  const i = lines.findIndex((l) => l.variantId === line.variantId);
  // Never reduce an existing quantity: an add that would exceed capacity is
  // refused and the line is left untouched.
  const plan = planAdd(i >= 0 ? lines[i].qty : null, qty, line.maxQty);
  if (!plan.ok) return { ok: false, reason: 'max' };
  if (i >= 0) {
    lines[i] = { ...lines[i], ...line, qty: plan.qty, unavailable: undefined };
    write(lines);
    return { ok: true, qty: plan.qty };
  }
  if (lines.length >= MAX_LINES) return { ok: false, reason: 'lines' };
  lines.push({ ...line, qty: plan.qty });
  write(lines);
  return { ok: true, qty: plan.qty };
}

export function setLineQty(variantId: string, qty: number): void {
  init();
  // Raising a quantity respects the cap; lowering one is always allowed
  // (that is how a shopper clears an over-cap line).
  const lines = snapshot.lines.map((l) => {
    if (l.variantId !== variantId) return l;
    const next = qty > l.qty ? Math.min(lineCap(l), qty) : Math.max(1, qty);
    return { ...l, qty: next };
  });
  write(lines);
}

export function removeLine(variantId: string): void {
  init();
  const before = snapshot.lines.find((l) => l.variantId === variantId);
  write(snapshot.lines.filter((l) => l.variantId !== variantId));
  if (before) {
    trackRemoveFromCart({
      item_id: before.productId,
      item_name: before.name,
      item_brand: before.brand,
      price: before.priceCents / 100,
      quantity: before.qty,
    });
  }
}

/**
 * After a paid order, remove the lines that order bought (see removeBought for
 * the rules). Reads the bag fresh from storage so another tab's edits are seen.
 */
export function removePurchasedLines(bought: BoughtLine[]): void {
  if (bought.length === 0) return;
  init();
  const current = read();
  const next = removeBought(current, bought);
  if (next.length !== current.length) write(next);
}

interface SyncVariant {
  priceCents: number;
  canBuy:     boolean;
  maxQty:     number;
  inStockQty: number;
  preorder:   boolean;
  shipsBy:    string | null;
  image?:     string;
  name?:      string;
}

/**
 * Re-price and re-check every line against the live catalog (the server route
 * reads the same cached PlazaStack feed the pages use). Lines whose variant is
 * gone or no longer buyable are flagged, never silently dropped. Quantities are
 * never changed here: a shopper who asked for 5 when 2 remain keeps 5, the line
 * is flagged (hasOverCap) and checkout stays blocked until they lower it.
 * The response is merged into the bag as it is NOW (not as it was when the
 * request left), so a line added, removed or cleared while the request was in
 * flight is never overwritten or resurrected. Best effort: on any failure the
 * bag is left as it was.
 */
export async function syncCartWithCatalog(): Promise<void> {
  init();
  const asked = snapshot.lines.map((l) => l.variantId);
  if (asked.length === 0) return;
  try {
    const res = await fetch('/api/cart-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantIds: asked }),
    });
    if (!res.ok) return;
    const { variants } = (await res.json()) as { variants: Record<string, SyncVariant> };
    const askedSet = new Set(asked);
    const current = snapshot.lines;
    const next = current.map((l) => {
      if (!askedSet.has(l.variantId)) return l; // added after the request left
      const v = variants?.[l.variantId];
      if (!v || !v.canBuy) return { ...l, unavailable: true };
      return {
        ...l,
        unavailable: undefined,
        priceCents: v.priceCents,
        maxQty: v.maxQty,
        inStockQty: v.inStockQty,
        preorder: v.preorder,
        shipsBy: v.shipsBy,
        name: v.name || l.name,
        image: v.image || l.image,
      };
    });
    if (JSON.stringify(next) !== JSON.stringify(current)) write(next);
  } catch {
    // leave the bag alone
  }
}
